import React, { useState, useEffect, useCallback } from "react";
import {
  InputLabel,
  TextField,
  Box,
  Grid,
  Divider,
  FormHelperText,
} from "@mui/material";
import FormikAutocomplete from "../../shared/AutoCompelete";
import * as yup from "yup";
import { useFormik, FormikProvider, FieldArray } from "formik";
import { direction, FieldTypeDropdown } from "../../../utils/constantDropdowns";
import CustomButton from "../../shared/CustomButton";
import PrimaryButton from "../../shared/PrimaryButton";
import LinkButton from "../../shared/LinkButton";
import { useDispatch } from "react-redux";
import { getFields, addNewRule, updateRule } from "../state/matchingAction";
import usePrompt from "../../Hooks/usePrompt";
import SaveChangesModal from "../../shared/SaveChangesModal";

import { useNavigate, useParams } from "react-router-dom";
const styleLabel = {
  color: "#050D2E",
  fontSize: ".875rem",
  fontWeight: "550",
  letterSpacing: "-0.5px",
  lineHeight: "16px",
  marginBottom: "8px",
};
const styleInput = {
  borderRadius: "4px",
  marginBottom: "15px",
  "& .MuiInputBase-root": {
    height: "37px",
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput": {
    height: "36px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #DBDEE6",
  },
};

const MatchingForm = ({
  messageTypes,
  fieldsP1,
  fieldsP2,
  CurrentRule,
  success,
  error,
}) => {
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const [messageId1, setSelectedMessageId1] = useState("");
  const [messageId2, setSelectedMessageId2] = useState("");
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [touched, setTouched] = useState(false);
  const validationSchema = yup.object().shape({
    ruleName: yup.string().required("rule name is required").nullable(),
    Rule: yup.array().of(
      yup.object().shape({
        FieldID1: yup
          .mixed()
          .notOneOf(["select fieldID"], "Field ID is required")
          .required("Field ID is required"),
        FieldID2: yup
          .mixed()
          .notOneOf(["select fieldID"], "Field ID is required")
          .required("Field ID is required"),
        ElementID1: yup
          .mixed()
          .nullable()
          .test("required", "Element ID is required", (value, context) => {
            return context.parent.FieldID1?.messageElements.length <= 0
              ? true
              : value?.elementId
              ? true
              : false;
          }),
        ElementID2: yup
          .mixed()
          .nullable()
          .test("required", "Element ID is required", (value, context) => {
            return context.parent.FieldID2?.messageElements.length <= 0
              ? true
              : value?.elementId
              ? true
              : false;
          }),
      })
    ),
    messageNumberP1: yup
      .mixed()
      .notOneOf(["select messageType"], "message type is required")
      .required("message type is required"),
    messageNumberP2: yup
      .mixed()
      .notOneOf(["select messageType"], "message type is required")
      .required("message type is required"),
    directionP1: yup
      .mixed()
      .notOneOf(["select direction"], "direction is required")
      .required("direction is required"),
    directionP2: yup
      .mixed()
      .notOneOf(["select direction"], "direction is required")
      .required("direction is required"),
  });

  //navigate to pendingLocation effect
  useEffect(() => {
    if (pendingLocation && !touched) Navigate(pendingLocation);
  }, [Navigate, pendingLocation, touched]);

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (nextLocation.pathname === "rules/") return true;
      if (touched) {
        setPendingLocation(nextLocation.pathname);
        setIsConfirmationModalOpen(true);
        return false;
      }
      return true;
    },
    [touched]
  );

  usePrompt(handleBlockedNavigation, touched);
  let test = useCallback(() => {
    let message1 = "";
    let message2 = "";
    if (CurrentRule?.messageId1 && CurrentRule.messageId2) {
      message1 = messageTypes.find((obj) => {
        return obj.messageTypeId === CurrentRule?.messageId1;
      });
      message2 = messageTypes.find((obj) => {
        return obj.messageTypeId === CurrentRule?.messageId2;
      });
    } else {
    }
    return { message1, message2 };
  }, [CurrentRule.messageId1, CurrentRule.messageId2, messageTypes]);

  const handleDiscardChanges = () => {
    setIsConfirmationModalOpen(false);
    setTouched(false);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      ruleName: CurrentRule.ruleName || "",
      messageNumberP1: test().message1 || null,
      messageNumberP2: test().message2 || null,
      directionP1: CurrentRule?.way1 || "",
      directionP2: CurrentRule.way2 || "",
      Rule:
        CurrentRule.matchingRuleFields.length > 0
          ? CurrentRule.matchingRuleFields.map((rule) => ({
              FieldID1: rule.filed1 || null,
              FieldID2: rule.field2 || null,
              ElementID1: rule.element1 || null,
              ElementID2: rule.element2 || null,
              FieldType:
                FieldTypeDropdown.filter(
                  (fieldType) => fieldType.id === CurrentRule.mandatory
                )[0] || FieldTypeDropdown[0],
            }))
          : [
              {
                FieldID1: null,
                FieldID2: null,
                ElementID1: null,
                ElementID2: null,
                FieldType: FieldTypeDropdown[0],
              },
            ],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      debugger;
      const data = {
        id: CurrentRule.id,
        ruleName: values.ruleName,
        messageId1: values.messageNumberP1.messageTypeId,
        messageId2: values.messageNumberP2.messageTypeId,
        way1: values.directionP1,
        way2: values.directionP2,
        matchingRuleFields: values.Rule.map((rule) => ({
          filedId1: rule.FieldID1.fieldId,
          fieldId2: rule.FieldID2.fieldId,
          elementId1: rule.ElementID1?.elementId || null,
          elementId2: rule?.ElementID2?.elementId || null,
          mandatory: rule.FieldType.id,
          operator:"EQUAL"
        })),
      };

      if (CurrentRule.id) {
        dispatch(updateRule(data, success, error)).then(() => {
          setIsConfirmationModalOpen(false);
          setTouched(false);
        });
      } else {
        dispatch(addNewRule(data, success, error)).then(() => {
          //navigate("/matching-rules")
          setIsConfirmationModalOpen(false);
          setTouched(false);
        });
      }
    },
  });

  const handleChange = (e) => {
    formik.handleChange(e);
    setTouched(true);
  };

  useEffect(() => {
    if (CurrentRule.messageId1) {
      setSelectedMessageId1(CurrentRule.messageId1);
    } else {
      dispatch({ type: "RESET_RULE" });
    }

    if (CurrentRule.messageId2) {
      setSelectedMessageId2(CurrentRule.messageId2);
    } else {
      dispatch({ type: "RESET_RULE" });
    }
  }, [CurrentRule.messageId1, CurrentRule.messageId2, dispatch]);

  useEffect(() => {
    if (messageId1 && messageId2) {
      dispatch(getFields(messageId1, "PartOne"));
      dispatch(getFields(messageId2, "PartTwo"));
    } else {
      dispatch({ type: "RESET_RULE" });
    }
  }, [messageId1, messageId2, dispatch]);

  const handleConfirmSaveChanges = () => {
    formik.handleSubmit();
    setIsConfirmationModalOpen(false);
  };

  return (
    <>
      <SaveChangesModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        discardChanges={handleDiscardChanges}
        saveChanges={handleConfirmSaveChanges}
      />
      <Grid sx={{ padding: "50px 30px 0 30px" }}>
        <InputLabel sx={styleLabel}>Rule Name</InputLabel>
        <TextField
          type="text"
          fullWidth
          sx={styleInput}
          value={formik.values.ruleName}
          name="ruleName"
          onChange={handleChange}
          helperText={formik.errors.ruleName}
          error={formik.errors.ruleName}
        />
        <Box>
          <InputLabel sx={styleLabel} style={{ margin: "10px 0 20px 0" }}>
            Message Part 1
          </InputLabel>
          <Box display={"grid"} gridTemplateColumns={"repeat(2,1fr)"} gap={2}>
            <Grid>
              <InputLabel sx={styleLabel}>Message Type</InputLabel>
              <FormikAutocomplete
                options={messageTypes}
                isOptionEqualToValue={(option, value) =>
                  option.messageTypeId === value.messageTypeId
                }
                getOptionLabel={(option) => option.messageTypeName}
                value={formik?.values?.messageNumberP1}
                name="messageNumberP1"
                formik={formik}
                label="Choose Type"
                height={"36px"}
                fullWidthFlag={true}
                validationOn={true}
                setTouched={setTouched}
                touched={touched}
                setChangeEvent={(e) => {
                  formik.values.Rule.forEach((rule, index) => {
                    return (
                      formik.setFieldValue(`Rule.${index}.FieldID1`, null),
                      formik.setFieldValue(`Rule.${index}.ElementID1`, null)
                    );
                  });
                  dispatch(getFields(e.messageTypeId, "PartOne"));
                }}
              />
            </Grid>

            <Grid>
              <InputLabel sx={styleLabel}>Way</InputLabel>

              <FormikAutocomplete
                options={direction.filter((dir) => dir !== "BOTH")}
                isOptionEqualToValue={(option, value) => option === value}
                value={formik.values.directionP1}
                name="directionP1"
                formik={formik}
                label="Choose Type"
                height={"36px"}
                fullWidthFlag={true}
                validationOn={true}
                setTouched={setTouched}
                touched={touched}
              />
            </Grid>
          </Box>

          <Box display={"grid"} gridTemplateColumns={"1fr 200px"} gap={2}></Box>
        </Box>

        <Box>
          <InputLabel sx={styleLabel} style={{ margin: "10px 0 20px 0" }}>
            Message Part 2
          </InputLabel>
          <Box display={"grid"} gridTemplateColumns={"repeat(2,1fr)"} gap={2}>
            <Grid>
              <InputLabel sx={styleLabel}>Message Type</InputLabel>
              <FormikAutocomplete
                options={messageTypes}
                isOptionEqualToValue={(option, value) =>
                  option.messageTypeId === value.messageTypeId
                }
                getOptionLabel={(option) => option.messageTypeName}
                value={formik?.values?.messageNumberP2}
                name="messageNumberP2"
                formik={formik}
                label="Choose Type"
                height={"36px"}
                fullWidthFlag={true}
                validationOn={true}
                setTouched={setTouched}
                touched={touched}
                setChangeEvent={(e) => {
                  formik.values.Rule.forEach((rule, index) => {
                    return (
                      formik.setFieldValue(`Rule.${index}.FieldID2`, null),
                      formik.setFieldValue(`Rule.${index}.ElementID2`, null)
                    );
                  });
                  dispatch(getFields(e.messageTypeId, "PartTwo"));
                }}
              />
            </Grid>

            <Grid>
              <InputLabel sx={styleLabel}>Way</InputLabel>

              <FormikAutocomplete
                options={direction.filter((dir) => dir !== "BOTH")}
                isOptionEqualToValue={(option, value) => option === value}
                value={formik.values.directionP2}
                name="directionP2"
                formik={formik}
                label="Choose Type"
                height={"36px"}
                fullWidthFlag={true}
                validationOn={true}
                setTouched={setTouched}
                touched={touched}
              />
            </Grid>
          </Box>
        </Box>
      </Grid>
      <Divider sx={{ marginTop: "20px", background: "#DBDEE6" }} />

      <Grid sx={{ padding: "30px 30px 0 30px" }}>
        <FormikProvider value={formik}>
          <FieldArray name="Rule">
            {({ insert, remove, push }) => (
              <Box>
                {formik.values.Rule.length > 0 &&
                  formik.values.Rule.map((friend, index) => (
                    <>
                      <Box
                        sx={{
                          backgroundColor: "#d4d6dd2e",
                          padding: "20px",
                          borderRadius: "8px",
                        }}
                        mb={2}
                        key={index}
                      >
                        <Box>
                          <Box
                            display={"grid"}
                            gridTemplateColumns={"repeat(2,1fr)"}
                            gap={2}
                          >
                            <Grid>
                              <InputLabel sx={styleLabel}>
                                Field ID 1
                              </InputLabel>
                              <FormikAutocomplete
                                options={fieldsP1}
                                isOptionEqualToValue={(option, value) =>
                                  option.fieldId === value?.fieldId
                                }
                                getOptionLabel={(option) => option?.fieldName}
                                value={
                                  formik.values.Rule[index]?.FieldID1 || null
                                }
                                name={`Rule.${index}.FieldID1`}
                                formik={formik}
                                label="Choose Field"
                                height={"36px"}
                                fullWidthFlag={true}
                                setTouched={setTouched}
                                touched={touched}
                                setChangeEvent={(e) => {
                                  formik.setFieldValue(
                                    `Rule.${index}.ElementID1`,
                                    null
                                  );
                                }}
                              />
                              <FormHelperText
                                sx={{
                                  color: "#d32f2f",
                                  marginTop: "-11px",
                                  marginBottom: "15px",
                                }}
                              >
                                {Boolean(formik.errors.Rule) &&
                                  formik.errors?.Rule[index]?.FieldID1}
                              </FormHelperText>
                            </Grid>

                            <Grid>
                              <InputLabel sx={styleLabel}>
                                Element ID 1
                              </InputLabel>

                              <FormikAutocomplete
                                options={
                                  formik.values.Rule[index]?.FieldID1
                                    ?.messageElements || []
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.elementId === value?.elementId
                                }
                                getOptionLabel={(option) => option?.elementName}
                                disabled={
                                  formik.values.Rule[index]?.FieldID1
                                    ?.messageElements?.length <= 0
                                }
                                value={formik.values.Rule[index].ElementID1}
                                name={`Rule.${index}.ElementID1`}
                                formik={formik}
                                label="Choose element"
                                height={"36px"}
                                fullWidthFlag={true}
                                setTouched={setTouched}
                                touched={touched}
                              />

                              <FormHelperText
                                sx={{
                                  color: "#d32f2f",
                                  marginTop: "-11px",
                                  marginBottom: "15px",
                                }}
                              >
                                {Boolean(formik.errors.Rule) &&
                                  formik.errors?.Rule[index]?.ElementID1}
                              </FormHelperText>
                            </Grid>
                          </Box>
                        </Box>
                        <Box mt={2}>
                          <Box
                            display={"grid"}
                            gridTemplateColumns={"repeat(2,1fr)"}
                            gap={2}
                          >
                            <Grid>
                              <InputLabel sx={styleLabel}>
                                Field ID 2
                              </InputLabel>
                              <FormikAutocomplete
                                options={fieldsP2}
                                isOptionEqualToValue={(option, value) =>
                                  option?.fieldId === value?.fieldId
                                }
                                getOptionLabel={(option) => option.fieldName}
                                value={formik.values.Rule[index]?.FieldID2}
                                name={`Rule.${index}.FieldID2`}
                                formik={formik}
                                label="Choose Field"
                                height={"36px"}
                                fullWidthFlag={true}
                                setTouched={setTouched}
                                touched={touched}
                                setChangeEvent={(e) => {
                                  formik.setFieldValue(
                                    `Rule.${index}.ElementID2`,
                                    null
                                  );
                                }}
                              />
                              <FormHelperText
                                sx={{
                                  color: "#d32f2f",
                                  marginTop: "-11px",
                                  marginBottom: "15px",
                                }}
                              >
                                {Boolean(formik.errors.Rule) &&
                                  formik.errors?.Rule[index]?.FieldID2}
                              </FormHelperText>
                            </Grid>

                            <Grid>
                              <InputLabel sx={styleLabel}>
                                Element ID 2
                              </InputLabel>

                              <FormikAutocomplete
                                options={
                                  formik.values.Rule[index]?.FieldID2
                                    ?.messageElements || []
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.elementId === value?.elementId
                                }
                                getOptionLabel={(option) => option?.elementName}
                                disabled={
                                  formik.values.Rule[index]?.FieldID2
                                    ?.messageElements?.length <= 0
                                }
                                value={formik.values.Rule[index].ElementID2}
                                name={`Rule.${index}.ElementID2`}
                                formik={formik}
                                label="Choose element"
                                height={"36px"}
                                fullWidthFlag={true}
                                setTouched={setTouched}
                                touched={touched}
                              />

                              <FormHelperText
                                sx={{
                                  color: "#d32f2f",
                                  marginTop: "-11px",
                                  marginBottom: "15px",
                                }}
                              >
                                {Boolean(formik.errors.Rule) &&
                                  formik.errors?.Rule[index]?.ElementID2}
                              </FormHelperText>
                            </Grid>
                          </Box>
                          {index !== 0 && (
                            <Grid>
                              <InputLabel sx={styleLabel}>
                                Field Type
                              </InputLabel>
                              <FormikAutocomplete
                                options={FieldTypeDropdown}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                getOptionLabel={(option) => option.name}
                                width="260px"
                                value={formik.values.Rule[index].FieldType}
                                name={`Rule.${index}.FieldType`}
                                formik={formik}
                                label="Choose Field Type"
                                height={"36px"}
                                setTouched={setTouched}
                                touched={touched}
                              />

                              <FormHelperText
                                sx={{
                                  color: "#d32f2f",
                                  marginTop: "-11px",
                                  marginBottom: "15px",
                                }}
                              >
                                {Boolean(formik.errors.Rule) &&
                                  formik.errors?.Rule[index]?.FieldType}
                              </FormHelperText>
                            </Grid>
                          )}
                        </Box>
                        <PrimaryButton
                          onClick={() => remove(index)}
                          disabled={formik?.values?.Rule.length === 1}
                        >
                          Remove
                        </PrimaryButton>
                      </Box>
                    </>
                  ))}
                <LinkButton
                  sx={{ marginBottom: "10px", fontSize: "14px" }}
                  color="#007AFF"
                  onClick={() =>
                    push({
                      FieldID1: null,
                      FieldID2: null,
                      ElementID1: null,
                      ElementID2: null,
                      FieldType: FieldTypeDropdown[0],
                    })
                  }
                >
                  + Add Another Criteria
                </LinkButton>
              </Box>
            )}
          </FieldArray>
        </FormikProvider>
      </Grid>

      <Grid mt={3} sx={{ padding: "50px 30px 0 30px" }}>
        <CustomButton
          text="Save Rule"
          disabled={!touched}
          action={() => formik.handleSubmit()}
        />
      </Grid>
    </>
  );
};

export default MatchingForm;
